import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Plane } from 'lucide-react';
import '../styles/MasterForms.css';
import apiClient from '../services/api';

const Airline = () => {
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  const yearId = session?.yearid;

  const [airlines, setAirlines] = useState([]);
  const [filteredAirlines, setFilteredAirlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [activeOnly, setActiveOnly] = useState(false);
  
  const [formData, setFormData] = useState({
    AirlineID: null,
    Airline: '',
    IsActive: true
  });
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!companyId || !yearId) {
      setLoading(false);
      return;
    }
    fetchAirlines();
  }, [companyId, yearId, activeOnly]);

  useEffect(() => {
    filterAirlines();
  }, [searchTerm, airlines]);

  const fetchAirlines = async () => {
    setLoading(true);
    try {
      const params = activeOnly ? { table: 'airline', active: '1' } : { table: 'airline' };
      const response = await apiClient.get('/LookupMaster.php', { params });
      
      if (response.data.success) {
        setAirlines(response.data.data || []);
      } else {
        alert('Failed to fetch airlines');
      }
    } catch (error) {
      console.error('Error fetching airlines:', error);
      alert('Error fetching airlines');
    } finally {
      setLoading(false);
    }
  };

  const filterAirlines = () => {
    if (!searchTerm.trim()) {
      setFilteredAirlines(airlines);
    } else {
      const filtered = airlines.filter(airline =>
        airline.Airline.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredAirlines(filtered);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.Airline.trim()) {
      newErrors.Airline = 'Airline name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setSubmitLoading(true);
    try {
      const payload = {
        ...formData,
        IsActive: formData.IsActive ? 1 : 0
      };
      
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/LookupMaster.php?table=airline', payload);
      
      if (response.data.success) {
        alert(editMode ? 'Airline updated successfully!' : 'Airline created successfully!');
        setShowModal(false);
        resetForm();
        fetchAirlines();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error saving airline:', error);
      alert('Error saving airline: ' + (error.response?.data?.message || error.message));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = async (airline) => {
    try {
      const response = await apiClient.get(`/LookupMaster.php?table=airline&id=${airline.AirlineID}`);
      if (response.data.success) {
        setFormData({
          AirlineID: response.data.data.AirlineID,
          Airline: response.data.data.Airline,
          IsActive: response.data.data.IsActive === 1
        });
        setEditMode(true);
        setShowModal(true);
        setErrors({});
      }
    } catch (error) {
      console.error('Error loading airline:', error);
      alert('Error loading airline details');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this airline?')) return;
    
    try {
      const response = await apiClient.delete('/LookupMaster.php?table=airline', {
        data: { AirlineID: id }
      });
      
      if (response.data.success) {
        alert('Airline deleted successfully!');
        fetchAirlines();
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error deleting airline:', error);
      alert('Error deleting airline');
    }
  };

  const resetForm = () => {
    setFormData({ AirlineID: null, Airline: '', IsActive: true });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  if (!companyId || !yearId) {
    return (
      <div className="master-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>Airline Master</h2>
          <p className="subtitle">Manage airline carriers</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add Airline
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search airlines..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        
        <div className="filter-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
            />
            <span>Active Only</span>
          </label>
        </div>

        <div className="toolbar-info">
          Total: {filteredAirlines.length}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading airlines...</div>
      ) : filteredAirlines.length === 0 ? (
        <div className="empty-state">
          <p>No airlines found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Airline</th>
                <th>Status</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAirlines.map((airline) => (
                <tr key={airline.AirlineID}>
                  <td className="font-mono">{airline.AirlineID}</td>
                  <td className="font-semibold">{airline.Airline}</td>
                  <td>
                    {airline.IsActive === 1 ? (
                      <span className="status-badge active">Active</span>
                    ) : (
                      <span className="status-badge inactive">Inactive</span>
                    )}
                  </td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(airline)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(airline.AirlineID)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Airline' : 'Add New Airline'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-section">
                <h4>
                  <Plane size={18} />
                  Airline Information
                </h4>
                
                <div className="form-group">
                  <label htmlFor="Airline">
                    Airline Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="Airline"
                    value={formData.Airline}
                    onChange={(e) => setFormData({ ...formData, Airline: e.target.value })}
                    maxLength="255"
                    placeholder="e.g., Emirates, Qatar Airways"
                    className={errors.Airline ? 'error' : ''}
                    autoFocus
                  />
                  {errors.Airline && (
                    <span className="error-message">{errors.Airline}</span>
                  )}
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.IsActive}
                      onChange={(e) => setFormData({ ...formData, IsActive: e.target.checked })}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Airline;